<script setup>
/**
 * ImmersiveDiscountBar
 * Vertical step ladder shown on the physical left of the immersive view.
 * Two variants sharing one scale/fill engine:
 *   - 'discount' (green, "הנחת כמות") — amount-discount tiers, fill = the
 *     cumulative paritMerakez group quantity.
 *   - 'promo' (red, "מבצע"; gold when `gold` for first-order promos) —
 *     madregot promotion steps, fill = the cumulative promo group quantity.
 * Checkpoints are positioned PROPORTIONALLY to their quantity threshold; each
 * shows the qty + the unit price at that tier. Tapping a tier emits
 * select-tier (the base checkpoint is a non-tappable reference).
 */
import { computed } from 'vue'

const props = defineProps({
  // [{ minQty, unitPriceAfterDiscount, isActive, isBase }] base-first
  ladder: { type: Array, default: () => [] },
  // Cumulative qty across the group (paritMerakez / promo makats).
  totalQty: { type: Number, default: 0 },
  // 'discount' (green) | 'promo' (red)
  variant: { type: String, default: 'discount' },
  // First-order promo styling (gold overrides the promo red).
  gold: { type: Boolean, default: false },
  // Caption text; defaults per variant.
  caption: { type: String, default: null },
})

defineEmits(['select-tier'])

const captionText = computed(
  () => props.caption || (props.variant === 'promo' ? 'מבצע מדרגות' : 'הנחת כמות')
)

// Minimum share of the rail reserved for the base → first-tier segment. The first
// discount tier is often a tiny quantity (e.g. 14 of 1680), so a purely
// proportional scale bunches it right on top of the base price. We floor that
// first gap to this fraction so the low end stays legible; the remaining tiers are
// mapped proportionally into the space above it (see posPct).
const MIN_FIRST_GAP_PCT = 16

// First non-base tier threshold (the lowest discount tier).
const firstTierQty = computed(() => {
  const firstTier = props.ladder.find((cp) => !cp.isBase)
  return firstTier ? Number(firstTier.minQty) || 0 : 0
})

// Top of the scale = the highest tier threshold.
const maxQty = computed(() => {
  const last = props.ladder[props.ladder.length - 1]
  return last ? Number(last.minQty) || 0 : 0
})

// Where the first tier sits on the rail: its proportional spot, but never closer
// to the base than the floor. When the tier is already comfortably spaced this is
// just its proportional position, so nothing distorts.
const firstTierPct = computed(() => {
  if (maxQty.value <= 0) return 0
  const raw = (firstTierQty.value / maxQty.value) * 100
  return Math.min(100, Math.max(raw, MIN_FIRST_GAP_PCT))
})

// Map a quantity to its % up the rail with a two-segment scale:
//   [0, firstTierQty]      → [0, firstTierPct]   (floored — base & first tier breathe)
//   [firstTierQty, maxQty] → [firstTierPct, 100] (proportional by actual quantity)
// Reduces exactly to a pure proportional scale when the first tier isn't crowded.
function posPct(qty) {
  const q = Number(qty) || 0
  if (maxQty.value <= 0 || q <= 0) return 0

  const fq = firstTierQty.value
  const fp = firstTierPct.value

  if (q <= fq) {
    return fq > 0 ? Math.max(0, Math.min(100, (q / fq) * fp)) : 0
  }

  const span = maxQty.value - fq
  if (span <= 0) return 100
  return Math.max(0, Math.min(100, fp + ((q - fq) / span) * (100 - fp)))
}

const fillPct = computed(() => posPct(props.totalQty))
</script>

<template>
  <div
    class="imm-disc"
    :class="[`imm-disc--${variant}`, { 'imm-disc--gold': variant === 'promo' && gold }]"
    @wheel.stop
  >
    <span class="imm-disc-caption">{{ captionText }}</span>
    <div class="imm-disc-track">
      <div class="imm-disc-rail"></div>
      <div class="imm-disc-fill" :style="{ height: fillPct + '%' }"></div>

      <button
        v-for="cp in ladder"
        :key="cp.minQty"
        type="button"
        class="imm-disc-cp"
        :class="{ reached: totalQty >= cp.minQty, active: cp.isActive, base: cp.isBase }"
        :style="{ bottom: posPct(cp.minQty) + '%' }"
        :disabled="cp.isBase"
        :aria-label="
          cp.isBase
            ? `מחיר בסיס ₪${cp.unitPriceAfterDiscount}`
            : `${cp.minQty} יחידות במחיר ₪${cp.unitPriceAfterDiscount}`
        "
        @click="!cp.isBase && $emit('select-tier', cp.minQty)"
      >
        <span class="imm-disc-dot"></span>
        <span class="imm-disc-info">
          <span class="imm-disc-qty">{{ cp.isBase ? 'בסיס' : `${cp.minQty} יח'` }}</span>
          <span class="imm-disc-price">₪{{ cp.unitPriceAfterDiscount }}</span>
        </span>
      </button>
    </div>
  </div>
</template>

<style scoped>
.imm-disc {
  flex: 0 0 auto;
  width: var(--imm-disc-w, 74px);
  /* Allow shrinking when placed as a PC grid item with minmax(0, ...) rows. */
  min-height: 0;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 6px;
  padding: 6px 4px 10px;
  -webkit-tap-highlight-color: transparent;
}

.imm-disc-caption {
  font-size: var(--imm-disc-caption, 0.62rem);
  font-weight: 700;
  text-align: center;
  letter-spacing: 0.02em;
  color: var(--imm-success, #34d399);
}

.imm-disc-track {
  position: relative;
  flex: 1;
  min-height: 0;
}

/* Rail + fill: a vertical track near the physical-left edge, centred under the
   checkpoint dots (dot column = 2px margin + dot width). Thickness is a token
   so PC can fatten it into a feature. */
.imm-disc-rail,
.imm-disc-fill {
  position: absolute;
  left: calc(2px + var(--imm-disc-dot, 13px) / 2 - var(--imm-disc-rail-w, 4px) / 2);
  width: var(--imm-disc-rail-w, 4px);
  border-radius: 999px;
}

.imm-disc-rail {
  top: 0;
  bottom: 0;
  background: var(--imm-field-border, rgba(255, 255, 255, 0.18));
}

.imm-disc-fill {
  bottom: 0;
  background: linear-gradient(180deg, #34d399 0%, #10b981 100%);
  box-shadow: 0 0 8px rgba(16, 185, 129, 0.45);
  transition: height 0.25s ease;
}

/* Checkpoint = dot on the rail + qty/price label toward the centre. */
.imm-disc-cp {
  position: absolute;
  left: 0;
  right: 0;
  transform: translateY(50%);
  display: flex;
  /* The overlay is dir=rtl; row-reverse keeps the dot on the physical-left rail
     with the label flowing toward centre (Hebrew label bidi preserved). */
  flex-direction: row-reverse;
  justify-content: flex-start;
  align-items: center;
  gap: 5px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  text-align: start;
  -webkit-tap-highlight-color: transparent;
}

.imm-disc-cp:disabled {
  cursor: default;
}

.imm-disc-cp:not(:disabled):active {
  transform: translateY(50%) scale(0.96);
}

.imm-disc-dot {
  flex-shrink: 0;
  width: var(--imm-disc-dot, 13px);
  height: var(--imm-disc-dot, 13px);
  margin-left: 2px;
  border-radius: 50%;
  background: var(--imm-bg-base, #0a0f1a);
  border: 2px solid var(--imm-field-border, rgba(255, 255, 255, 0.35));
  transition:
    background 0.2s ease,
    border-color 0.2s ease,
    transform 0.2s ease;
}

.imm-disc-cp.reached .imm-disc-dot {
  background: #10b981;
  border-color: #34d399;
}

.imm-disc-cp.active .imm-disc-dot {
  transform: scale(1.25);
  box-shadow: 0 0 0 4px rgba(16, 185, 129, 0.22);
}

.imm-disc-info {
  display: flex;
  flex-direction: column;
  line-height: 1.05;
  min-width: 0;
}

.imm-disc-qty {
  font-size: var(--imm-disc-qty-font, 0.6rem);
  font-weight: 600;
  color: var(--imm-text-muted, rgba(255, 255, 255, 0.6));
  white-space: nowrap;
}

.imm-disc-price {
  font-size: var(--imm-disc-price-font, 0.72rem);
  font-weight: 800;
  color: var(--imm-text-soft, rgba(255, 255, 255, 0.85));
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

/* Reached / active checkpoints read brighter. */
.imm-disc-cp.reached .imm-disc-qty {
  color: var(--imm-text-soft, rgba(255, 255, 255, 0.82));
}

.imm-disc-cp.reached .imm-disc-price,
.imm-disc-cp.active .imm-disc-price {
  color: var(--imm-success, #34d399);
}

/* One-shot "absorb" pulse — the class is added straight on this element by
   ImmersiveCatalog when the flying celebration badge lands in the dot (the
   scope attribute is on the element, so the external class still matches).
   Starts swollen (as if it just swallowed the badge) and settles back to the
   .active scale while a glow ring dissolves outward. */
.imm-disc-dot.imm-disc-dot-absorb {
  --absorb-glow: rgba(52, 211, 153, 0.65);
  --absorb-glow-off: rgba(52, 211, 153, 0);
  --absorb-rest-ring: rgba(16, 185, 129, 0.22);
  animation: imm-disc-absorb 0.55s cubic-bezier(0.16, 1, 0.3, 1) both;
}

@keyframes imm-disc-absorb {
  0% {
    transform: scale(1.9);
    box-shadow:
      0 0 0 4px var(--absorb-rest-ring),
      0 0 16px 7px var(--absorb-glow);
  }
  100% {
    transform: scale(1.25);
    box-shadow:
      0 0 0 4px var(--absorb-rest-ring),
      0 0 22px 12px var(--absorb-glow-off);
  }
}

/* ── Promo (madregot) variant — red identity, matching the "מבצע" badge ── */
.imm-disc--promo .imm-disc-caption {
  color: #ef4444;
}

.imm-disc--promo .imm-disc-fill {
  background: linear-gradient(180deg, #f87171 0%, #dc2626 100%);
  box-shadow: 0 0 8px rgba(220, 38, 38, 0.45);
}

.imm-disc--promo .imm-disc-cp.reached .imm-disc-dot {
  background: #dc2626;
  border-color: #f87171;
}

.imm-disc--promo .imm-disc-cp.active .imm-disc-dot {
  box-shadow: 0 0 0 4px rgba(220, 38, 38, 0.22);
}

.imm-disc--promo .imm-disc-cp.reached .imm-disc-price,
.imm-disc--promo .imm-disc-cp.active .imm-disc-price {
  color: #ef4444;
}

.imm-disc--promo .imm-disc-dot.imm-disc-dot-absorb {
  --absorb-glow: rgba(248, 113, 113, 0.65);
  --absorb-glow-off: rgba(248, 113, 113, 0);
  --absorb-rest-ring: rgba(220, 38, 38, 0.22);
}

/* ── First-order promo — gold identity, matching the gold badge ── */
.imm-disc--gold .imm-disc-caption {
  color: #f59e0b;
}

.imm-disc--gold .imm-disc-fill {
  background: linear-gradient(180deg, #fcd34d 0%, #d97706 100%);
  box-shadow: 0 0 8px rgba(245, 158, 11, 0.45);
}

.imm-disc--gold .imm-disc-cp.reached .imm-disc-dot {
  background: #d97706;
  border-color: #fcd34d;
}

.imm-disc--gold .imm-disc-cp.active .imm-disc-dot {
  box-shadow: 0 0 0 4px rgba(245, 158, 11, 0.22);
}

.imm-disc--gold .imm-disc-cp.reached .imm-disc-price,
.imm-disc--gold .imm-disc-cp.active .imm-disc-price {
  color: #f59e0b;
}

.imm-disc--gold .imm-disc-dot.imm-disc-dot-absorb {
  --absorb-glow: rgba(252, 211, 77, 0.7);
  --absorb-glow-off: rgba(252, 211, 77, 0);
  --absorb-rest-ring: rgba(245, 158, 11, 0.22);
}

/* ── PC (≥1280): the rail is a real full-height grid column. The end
   checkpoints hang HALF their height past the track ends (bottom 0%/100% +
   translateY(50%)) — reserve that overhang inside the track, otherwise the
   base label clips at the frame bottom and the top tier collides with the
   caption / leaks out of the pane into the neighbor's view (the surface
   overflow-clips it). Dots get a heavier border to match the thicker rail
   (--imm-disc-rail-w / bigger --imm-disc-dot come from the overlay tokens). */
@media (min-width: 1280px) {
  .imm-disc-track {
    margin-block: 18px 14px;
  }

  .imm-disc-dot {
    border-width: 3px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .imm-disc-fill,
  .imm-disc-dot {
    transition: none;
  }
}
</style>
